import type { TaskStatus } from './enums'
import { isOpen } from './visibility'

/**
 * Fluxo da tarefa no quadro, no molde do Kanban de projetos do Hub SISTEMAS:
 * Pendente é a coluna de entrada, Em processo a intermediária, Realizada a de
 * conclusão e Cancelada a saída lateral. O banco aceita qualquer transição
 * (`supply.move_task`); aqui a tela sabe qual é o próximo passo e o que pedir
 * antes de mover.
 */

export interface TaskFlow {
  /** Próximo passo que não fecha a tarefa (Pendente → Em processo). */
  readonly advance?: TaskStatus
  /** Fechar como realizada, de qualquer status aberto. */
  readonly conclude?: TaskStatus
  /** Fechar sem fazer — pede motivo. */
  readonly cancel?: TaskStatus
  /** Para onde volta ao reabrir: realizada continua o trabalho; cancelada recomeça. */
  readonly reopen?: TaskStatus
}

export function taskFlow(status: TaskStatus): TaskFlow {
  if (status === 'pendente') return { advance: 'em_processo', conclude: 'realizada', cancel: 'cancelada' }
  if (status === 'em_processo') return { conclude: 'realizada', cancel: 'cancelada' }
  if (status === 'realizada') return { reopen: 'em_processo' }
  return { reopen: 'pendente' }
}

/** O que a tela pergunta antes de gravar a mudança de status. */
export type MoveRequirement = 'quantidade' | 'motivo' | null

/**
 * Realizada com mais de uma unidade pergunta quanto foi entregue (é o número
 * que o desempenho mede). Cancelar pede o motivo. O resto move direto.
 */
export function moveRequirement(
  task: { readonly status: TaskStatus; readonly qtd_prevista: number },
  to: TaskStatus,
): MoveRequirement {
  if (to === task.status) return null
  if (to === 'realizada' && task.qtd_prevista > 1) return 'quantidade'
  if (to === 'cancelada') return 'motivo'
  return null
}

export interface BoardFilters {
  /** Só as minhas: executo ou sou o responsável (mesma regra de "Minha semana"). */
  readonly minhas: boolean
  readonly atrasadas: boolean
  readonly semExecutor: boolean
  /** Texto no título ou na chave. */
  readonly q: string
}

export const EMPTY_BOARD_FILTERS: BoardFilters = { minhas: false, atrasadas: false, semExecutor: false, q: '' }

export function hasBoardFilters(f: BoardFilters): boolean {
  return f.minhas || f.atrasadas || f.semExecutor || f.q.trim() !== ''
}

export interface FilterableTask {
  readonly key: string
  readonly title: string
  readonly status: TaskStatus
  readonly prazo: string | null
  readonly executor_id: string | null
  readonly executor_text: string | null
  readonly responsavel_id: string | null
}

/** `today` em `YYYY-MM-DD`; atrasada é aberta com prazo antes de hoje. */
export function filterBoardTasks<T extends FilterableTask>(
  tasks: readonly T[],
  f: BoardFilters,
  memberId: string,
  today: string,
): T[] {
  const q = f.q.trim().toLowerCase()
  return tasks.filter((t) => {
    if (f.minhas && t.executor_id !== memberId && t.responsavel_id !== memberId) return false
    if (f.atrasadas && !(isOpen(t.status) && t.prazo !== null && t.prazo < today)) return false
    if (f.semExecutor && (t.executor_id !== null || (t.executor_text ?? '').trim() !== '')) return false
    if (q && !t.title.toLowerCase().includes(q) && !t.key.toLowerCase().includes(q)) return false
    return true
  })
}
